import { useEffect, useMemo, useState } from "react";
import { Trash2, Upload, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = ["hero", "about", "services", "gallery", "contact", "carousel"];

export default function AdminPhotos() {
  const { t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [section, setSection] = useState("carousel");
  const [altNl, setAltNl] = useState("");
  const [altEn, setAltEn] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/admin/photos").then(({ data }) => setPhotos(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    SECTIONS.forEach((s) => (map[s] = []));
    photos.forEach((p) => { (map[p.section] = map[p.section] || []).push(p); });
    Object.keys(map).forEach((s) => map[s].sort((a, b) => a.order - b.order));
    return map;
  }, [photos]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    const nextOrder = (grouped[section] || []).length;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("section", section);
    formData.append("alt_text_nl", altNl);
    formData.append("alt_text_en", altEn);
    formData.append("order", String(nextOrder));
    try {
      await api.post("/admin/photos", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null); setAltNl(""); setAltEn("");
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setListError("");
    try {
      await api.delete(`/admin/photos/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setListError(formatApiErrorDetail(err.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  const swapOrder = async (a, b) => {
    setListError("");
    const formA = new FormData(); formA.append("order", String(b.order));
    const formB = new FormData(); formB.append("order", String(a.order));
    try {
      await Promise.all([
        api.put(`/admin/photos/${a.id}`, formA, { headers: { "Content-Type": "multipart/form-data" } }),
        api.put(`/admin/photos/${b.id}`, formB, { headers: { "Content-Type": "multipart/form-data" } }),
      ]);
      load();
    } catch (err) {
      setListError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const moveUp = (list, idx) => idx > 0 && swapOrder(list[idx], list[idx - 1]);
  const moveDown = (list, idx) => idx < list.length - 1 && swapOrder(list[idx], list[idx + 1]);

  return (
    <div data-testid="admin-photos-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-white mb-8">{t("admin.photos.title")}</h1>

      <form onSubmit={handleUpload} className="bg-[#141414] border border-white/10 rounded-lg p-5 mb-10 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-white/70 text-sm">{t("admin.photos.section")}</Label>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger data-testid="photo-section-select" className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5 w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
              {SECTIONS.map((s) => <SelectItem key={s} value={s}>{t(`admin.photos.sections.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/70 text-sm">{t("admin.photos.altTextNl")}</Label>
          <Input data-testid="photo-alt-nl-input" value={altNl} onChange={(e) => setAltNl(e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5 w-40" />
        </div>
        <div>
          <Label className="text-white/70 text-sm">{t("admin.photos.altTextEn")}</Label>
          <Input data-testid="photo-alt-en-input" value={altEn} onChange={(e) => setAltEn(e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5 w-40" />
        </div>
        <div>
          <Label className="text-white/70 text-sm">{t("admin.photos.upload")}</Label>
          <input data-testid="photo-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="block text-white/70 text-sm mt-1.5" />
        </div>
        <Button type="submit" data-testid="photo-upload-submit" disabled={!file || uploading} className="bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-50">
          <Upload size={16} className="mr-1.5" /> {uploading ? t("common.loading") : t("admin.photos.upload")}
        </Button>
      </form>

      {error && <p data-testid="photo-upload-error" className="text-red-400 text-sm mb-6">{error}</p>}
      {listError && <p data-testid="photo-list-error" className="text-red-400 text-sm mb-6">{listError}</p>}

      {photos.length === 0 && <p className="text-white/40" data-testid="photos-empty-state">{t("admin.photos.noPhotos")}</p>}

      {SECTIONS.map((s) => {
        const list = grouped[s] || [];
        if (list.length === 0) return null;
        return (
          <div key={s} className="mb-10">
            <h2 className="text-white text-lg mb-4">{t(`admin.photos.sections.${s}`)}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {list.map((p, idx) => (
                <div key={p.id} data-testid={`photo-card-${p.id}`} className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden">
                  <img src={`${BACKEND_URL}${p.url}`} alt={p.alt_text_nl} className="w-full h-32 object-cover" />
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex gap-1">
                      <button data-testid={`photo-move-up-${p.id}`} disabled={idx === 0} onClick={() => moveUp(list, idx)} title={t("admin.photos.moveUp")} className="text-white/50 hover:text-gold disabled:opacity-20"><ArrowUp size={14} /></button>
                      <button data-testid={`photo-move-down-${p.id}`} disabled={idx === list.length - 1} onClick={() => moveDown(list, idx)} title={t("admin.photos.moveDown")} className="text-white/50 hover:text-gold disabled:opacity-20"><ArrowDown size={14} /></button>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`photo-edit-${p.id}`} onClick={() => setEditingPhoto(p)} title={t("admin.photos.edit")} className="text-white/50 hover:text-gold"><Pencil size={14} /></button>
                      <button data-testid={`photo-delete-${p.id}`} onClick={() => setDeleteTarget(p)} title={t("admin.photos.deleteConfirm")} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <EditPhotoDialog photo={editingPhoto} onClose={() => setEditingPhoto(null)} onSaved={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.photos.deleteConfirm")}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="photo-delete-cancel" className="bg-transparent border-white/20 text-white hover:bg-white/10">{t("admin.photos.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="photo-delete-confirm" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">{t("admin.bookings.deleteBooking")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditPhotoDialog({ photo, onClose, onSaved }) {
  const { t } = useLanguage();
  const [altNl, setAltNl] = useState("");
  const [altEn, setAltEn] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (photo) {
      setAltNl(photo.alt_text_nl || "");
      setAltEn(photo.alt_text_en || "");
      setNewFile(null);
      setError("");
    }
  }, [photo]);

  const handleSave = async () => {
    if (!photo) return;
    setSaving(true);
    setError("");
    const formData = new FormData();
    formData.append("alt_text_nl", altNl);
    formData.append("alt_text_en", altEn);
    if (newFile) formData.append("file", newFile);
    try {
      await api.put(`/admin/photos/${photo.id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!photo} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="photo-edit-dialog" className="bg-[#141414] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>{t("admin.photos.editPhoto")}</DialogTitle>
        </DialogHeader>
        {photo && (
          <div className="space-y-4">
            <img src={`${BACKEND_URL}${photo.url}`} alt={photo.alt_text_nl} className="w-full h-40 object-cover rounded-md" />
            <div>
              <Label className="text-white/70 text-sm">{t("admin.photos.altTextNl")}</Label>
              <Input data-testid="photo-edit-alt-nl-input" value={altNl} onChange={(e) => setAltNl(e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">{t("admin.photos.altTextEn")}</Label>
              <Input data-testid="photo-edit-alt-en-input" value={altEn} onChange={(e) => setAltEn(e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">{t("admin.photos.replaceImage")}</Label>
              <input data-testid="photo-edit-file-input" type="file" accept="image/*" onChange={(e) => setNewFile(e.target.files[0])} className="block text-white/70 text-sm mt-1.5" />
            </div>
            {error && <p data-testid="photo-edit-error" className="text-red-400 text-sm">{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="bg-transparent border-white/20 text-white hover:bg-white/10 rounded-none">
                {t("admin.photos.cancel")}
              </Button>
              <Button type="button" data-testid="photo-edit-save" onClick={handleSave} disabled={saving} className="bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-50">
                {saving ? t("admin.photos.saving") : t("admin.photos.save")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
